import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import api from '../services/api';
import { useTheme } from '../hooks/useTheme';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';
import MemberSelector from '../components/MemberSelector';
import ManageAccessModal from '../components/ManageAccessModal';

// Leaflet
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { initLeafletIcons } from '../utils/leafletSetup';

// Registra componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function DashboardPage() {
  initLeafletIcons();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [period, setPeriod] = React.useState('6m');
  const [isSelectMemberModalOpen, setIsSelectMemberModalOpen] = React.useState(false);
  const [isManageAccessModalOpen, setIsManageAccessModalOpen] = React.useState(false);
  const [selectedMemberId, setSelectedMemberId] = React.useState(null);
  const [selectedMemberObj, setSelectedMemberObj] = React.useState(null);
  
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard', period],
    queryFn: async () => {
      const response = await api.get('/dashboard', { params: { period } });
      return response.data.data;
    },
    refetchInterval: 5 * 60 * 1000, // Refaz a cada 5 minutos
  });

  const { data: cellsResponse, isLoading: isLoadingCells } = useQuery({
    queryKey: ['cells-map'],
    queryFn: async () => {
      const response = await api.get('/cells', { params: { per_page: 999, is_active: 1 } });
      return response.data;
    }
  });

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: async () => {
      const response = await api.get('/tenants/me');
      return response.data.data;
    }
  });
  
  const calendarIframe = tenantData?.settings?.calendar_iframe || '';

  const { data: rolesResponse } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await api.get('/roles');
      return response.data;
    }
  });
  const roles = rolesResponse?.data || [];

  if (isLoading) {
    return (
      <AppLayout>
        <div className="dashboard-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="dashboard-error" style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Erro ao carregar dashboard</h2>
          <p>{error.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Recarregar
          </button>
        </div>
      </AppLayout>
    );
  }

  const kpis = dashboardData?.kpis || {};
  const growthChart = dashboardData?.growth_chart || { labels: [], datasets: [] };
  const upcomingMultiplications = dashboardData?.upcoming_multiplications || [];
  const recentActivity = dashboardData?.recent_activity || [];
  const cells = Array.isArray(cellsResponse?.data) ? cellsResponse.data : [];

  // Filtrar apenas células que possuem latitude e longitude válidas
  const mappedCells = cells.filter(cell => {
    if (!cell.is_active || !cell.latitude || !cell.longitude) return false;
    const lat = parseFloat(cell.latitude);
    const lng = parseFloat(cell.longitude);
    return !isNaN(lat) && !isNaN(lng);
  });

  // Calcula centro do mapa baseado nas células (ou usa default se vazio)
  const defaultCenter = [38.7223, -9.1393]; // Lisboa como fallback inicial

  let mapCenter = defaultCenter;
  if (mappedCells.length > 0) {
    const validCells = mappedCells.map(c => ({ lat: parseFloat(c.latitude), lng: parseFloat(c.longitude) }));
    const sumLat = validCells.reduce((sum, cell) => sum + cell.lat, 0);
    const sumLng = validCells.reduce((sum, cell) => sum + cell.lng, 0);
    mapCenter = [sumLat / validCells.length, sumLng / validCells.length];
  }

  const membersDataset = growthChart.datasets?.find(d => d.label === 'Membros') || { data: [] };
  const visitorsDataset = growthChart.datasets?.find(d => d.label === 'Visitantes') || { data: [] };
  const faDataset = growthChart.datasets?.find(d => d.label === 'F.A.') || { data: [] };

  // Configuração do gráfico de crescimento
  const growthChartData = {
    labels: growthChart.labels || [],
    datasets: [
      {
        label: 'Membros',
        data: membersDataset.data,
        borderColor: '#36B37E',
        backgroundColor: 'rgba(54, 179, 126, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'F.A.',
        data: faDataset.data,
        borderColor: '#00B8D9',
        backgroundColor: 'rgba(0, 184, 217, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Visitantes',
        data: visitorsDataset.data,
        borderColor: '#FF8B00',
        backgroundColor: 'rgba(255, 139, 0, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme === 'dark' ? '#f0f6fc' : '#172B4D',
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: theme === 'dark' ? '#30363d' : '#EBECF0',
        },
        ticks: {
          color: theme === 'dark' ? '#8b949e' : '#6B778C',
        },
      },
      y: {
        grid: {
          color: theme === 'dark' ? '#30363d' : '#EBECF0',
        },
        ticks: {
          color: theme === 'dark' ? '#8b949e' : '#6B778C',
        },
      },
    },
  };

  return (
    <AppLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">Visão geral do seu ministério • Atualizado há 5 minutos</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/checkpoint/select')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
        >
          <span className="material-symbols-outlined">check_circle</span>
          Iniciar Checkpoint
        </button>
      </div>

      {/* Period Filter */}
      <div style={{ display: 'flex', marginBottom: '1rem' }}>
        <div style={{ 
          display: 'flex', 
          background: 'var(--surface)', 
          padding: '4px', 
          borderRadius: '8px', 
          border: '1px solid var(--border)',
          gap: '4px'
        }}>
          {['1m', '3m', '6m', 'total'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: 'none',
                background: period === p ? 'var(--primary)' : 'transparent',
                color: period === p ? 'white' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: period === p ? '600' : '400',
                fontSize: '0.875rem',
                transition: 'all 0.2s'
              }}
            >
              {p === '1m' ? '1m' : p === '3m' ? '3m' : p === '6m' ? '6m' : 'Total'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon success">
            <span className="material-symbols-outlined">groups</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.total_members?.value || 0}</span>
            <span className="stat-label">Total de Membros</span>
          </div>
          {kpis.total_members?.change_percent != null && (
            <div className={`stat-change ${kpis.total_members.change_percent >= 0 ? 'positive' : 'negative'}`}>
              <span className="material-symbols-outlined">
                {kpis.total_members.change_percent >= 0 ? 'trending_up' : 'trending_down'}
              </span>
              {Math.abs(kpis.total_members.change_percent)}%
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon primary">
            <span className="material-symbols-outlined">home_work</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.total_cells?.value || 0}</span>
            <span className="stat-label">Células Ativas</span>
          </div>
          {kpis.total_cells?.change_percent != null && (
            <div className={`stat-change ${kpis.total_cells.change_percent >= 0 ? 'positive' : 'negative'}`}>
              <span className="material-symbols-outlined">
                {kpis.total_cells.change_percent >= 0 ? 'trending_up' : 'trending_down'}
              </span>
              {Math.abs(kpis.total_cells.change_percent)}%
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">
            <span className="material-symbols-outlined">person_add</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.visitors_30_days?.value || 0}</span>
            <span className="stat-label">Visitantes</span>
          </div>
          {kpis.visitors_30_days?.change_percent != null && (
            <div className={`stat-change ${kpis.visitors_30_days.change_percent >= 0 ? 'positive' : 'negative'}`}>
              <span className="material-symbols-outlined">
                {kpis.visitors_30_days.change_percent >= 0 ? 'trending_up' : 'trending_down'}
              </span>
              {Math.abs(kpis.visitors_30_days.change_percent)}%
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon info">
            <span className="material-symbols-outlined">emoji_people</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.total_fa?.value || 0}</span>
            <span className="stat-label">F.A.</span>
          </div>
          {kpis.total_fa?.change_percent != null && (
            <div className={`stat-change ${kpis.total_fa.change_percent >= 0 ? 'positive' : 'negative'}`}>
              <span className="material-symbols-outlined">
                {kpis.total_fa.change_percent >= 0 ? 'trending_up' : 'trending_down'}
              </span>
              {Math.abs(kpis.total_fa.change_percent)}%
            </div>
          )}
        </div>
      </div>

      {/* Second Row of Cards - Course Metrics */}
      <div className="stats-grid" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="stat-card">
          <div className="stat-icon info">
            <span className="material-symbols-outlined">menu_book</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.cme_percent?.value !== undefined ? `${kpis.cme_percent.value}%` : '0%'}</span>
            <span className="stat-label">% de CME</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon warning">
            <span className="material-symbols-outlined">diversity_3</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.ctl_percent?.value !== undefined ? `${kpis.ctl_percent.value}%` : '0%'}</span>
            <span className="stat-label">% de CTL</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon danger">
            <span className="material-symbols-outlined">workspace_premium</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{kpis.stp_percent?.value !== undefined ? `${kpis.stp_percent.value}%` : '0%'}</span>
            <span className="stat-label">% de STP</span>
          </div>
        </div>
        
        {/* Card Gestão de Acessos */}
        <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div className="stat-icon primary">
              <span className="material-symbols-outlined">vpn_key</span>
            </div>
            <div>
              <span className="stat-value" style={{ fontSize: '1.25rem' }}>Acessos</span>
              <span className="stat-label">Gestão de credenciais</span>
            </div>
          </div>
          <button 
            className="btn btn-primary btn-sm" 
            style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}
            onClick={() => setIsSelectMemberModalOpen(true)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>lock_reset</span>
            Repor passe de utilizador
          </button>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Growth Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Crescimento de Membros</h3>
            {/* The select here was removed because the global period toggle above now controls this chart too */}
          </div>
          <div className="chart-container">
            <Line data={growthChartData} options={chartOptions} />
          </div>
        </div>

        {/* Próximas Multiplicações */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Próximas Multiplicações</h3>
            <span className="badge badge-info" style={{ backgroundColor: 'var(--info-light)', color: 'var(--info)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>
              +12 membros
            </span>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {upcomingMultiplications.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-light)', textAlign: 'left', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '0.75rem 0.5rem', fontWeight: '600' }}>Célula</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontWeight: '600' }}>Líder</th>
                    <th style={{ padding: '0.75rem 0.5rem', fontWeight: '600' }}>Discipulador</th>
                    <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: '600' }}>Membros</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingMultiplications.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border-light)', height: '48px' }}>
                      <td style={{ padding: '0.75rem 0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {item.cell_name}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', color: 'var(--text-secondary)' }}>
                        {item.leader_name || 'Sem líder'}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', color: 'var(--text-secondary)' }}>
                        {item.discipulador_name || 'Sem discipulador'}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 'bold', color: 'var(--primary)' }}>
                        {item.members_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
                Nenhuma célula com mais de 12 membros
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Lower Section */}
      <div className="lower-section">
        {/* Map */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Mapa de Células</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/cells/map')}>
              <span className="material-symbols-outlined">fullscreen</span>
            </button>
          </div>
          <div style={{ height: '250px', width: '100%', position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
            {isLoadingCells ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <LoadingSpinner size="md" />
              </div>
            ) : mappedCells.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>satellite_alt</span>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Nenhuma célula no mapa</p>
              </div>
            ) : (
              <MapContainer
                key={mapCenter.join(',')}
                center={mapCenter}
                zoom={11}
                style={{ height: '100%', width: '100%', zIndex: 1 }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {mappedCells.map(cell => (
                  <Marker
                    key={cell.id}
                    position={[parseFloat(cell.latitude), parseFloat(cell.longitude)]}
                  >
                    <Popup>
                      <div style={{ padding: '0.25rem', minWidth: '120px' }}>
                        <h4 style={{ margin: '0 0 0.25rem 0', fontWeight: 'bold', fontSize: '0.875rem' }}>{cell.name}</h4>
                        {cell.address && <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{cell.address}</p>}
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/cells/${cell.id}`);
                          }}
                          style={{ fontSize: '0.75rem', padding: '0.125rem 0.25rem', width: '100%' }}
                        >
                          Ver Célula
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Atividade Recente</h3>
          </div>
          <div className="activity-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="activity-item" style={{ marginBottom: '1rem' }}>
                  <div className={`activity-icon ${activity.type || 'info'}`}>
                    <span className="material-symbols-outlined">{activity.icon || 'info'}</span>
                  </div>
                  <div className="activity-content">
                    <div className="activity-title" style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                      {activity.title}
                    </div>
                    {activity.subtitle && (
                      <div className="activity-subtitle" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: '2px' }}>
                        {activity.subtitle}
                      </div>
                    )}
                    <div className="activity-time" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                      {activity.time}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
                Nenhuma atividade recente
              </p>
            )}
          </div>
        </div>

        {/* Agenda */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Agenda</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agenda')}>
              <span className="material-symbols-outlined">fullscreen</span>
            </button>
          </div>
          <div style={{ height: '250px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
            {calendarIframe ? (
              <div 
                style={{ width: '100%', height: '100%' }}
                className="calendar-wrapper"
                dangerouslySetInnerHTML={{ __html: calendarIframe.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[^"]*"/, 'height="100%"') }}
              />
            ) : (
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-muted)'
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: '36px' }}>
                  calendar_month
                </span>
                <p style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>Agenda não configurada</p>
                <button 
                  className="btn btn-outline btn-sm mt-2" 
                  onClick={() => navigate('/settings')}
                  style={{ marginTop: '10px' }}
                >
                  Configurar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Select Member Modal */}
      {isSelectMemberModalOpen && (
        <div className="modal-overlay active" style={{ zIndex: 1050 }}>
          <div className="modal" style={{ background: 'var(--surface)', color: 'var(--text-primary)', maxWidth: '400px', textAlign: 'left', overflow: 'visible' }}>
            <div className="modal-header">
              <h2 className="modal-title">Selecionar Utilizador</h2>
              <button className="modal-close" onClick={() => setIsSelectMemberModalOpen(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ minHeight: '250px' }}>
              <p className="mb-4 text-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                Pesquise o membro para alterar os seus acessos.
              </p>
              <MemberSelector
                value={selectedMemberId}
                onChange={(id, member) => {
                  setSelectedMemberId(id);
                  setSelectedMemberObj(member);
                }}
              />
              <div className="modal-footer" style={{ marginTop: '40px', display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setIsSelectMemberModalOpen(false)}>Cancelar</button>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1 }} 
                  disabled={!selectedMemberId} 
                  onClick={() => {
                    setIsSelectMemberModalOpen(false);
                    setIsManageAccessModalOpen(true);
                  }}
                >
                  Avançar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Access Modal */}
      <ManageAccessModal 
        isOpen={isManageAccessModalOpen}
        onClose={() => setIsManageAccessModalOpen(false)}
        member={selectedMemberObj}
        roles={roles}
        onSuccess={() => {
          setIsManageAccessModalOpen(false);
          setSelectedMemberId(null);
          setSelectedMemberObj(null);
        }}
      />
    </AppLayout>
  );
}

export default DashboardPage;
